/**
 * Deploy-time configuration for the Next.js app.
 * Values come from env vars. Client-visible ones use `NEXT_PUBLIC_*`.
 */

import { SITE } from '@/seo/site';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function readEnv(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
}

/**
 * Browser always posts here (same origin). The route proxies to the tablet
 * backend so production domains are not blocked by upstream CORS.
 */
export const CLIENT_LOGIN_PATH = '/api/auth/login/';

/**
 * Upstream tablet-app API origin (server-side proxy target).
 * Prefer `API_ORIGIN` (not `NEXT_PUBLIC_*`) so the hostname stays off the client bundle.
 */
export const UPSTREAM_API_ORIGIN = trimTrailingSlash(
  readEnv(
    process.env.API_ORIGIN ?? process.env.NEXT_PUBLIC_API_ORIGIN,
    'https://api.spk2go.mobi',
  ),
);

/** Upstream SimpleJWT login path used by the tablet app. */
export const UPSTREAM_LOGIN_PATH = readEnv(
  process.env.API_LOGIN_PATH ?? process.env.NEXT_PUBLIC_LOGIN_PATH,
  '/api/token/',
);

/** SMS OTP second step after `/api/token/` returns `otp_required` (same paths as the SPK2GO back office). */
export const UPSTREAM_OTP_VERIFY_PATH = '/security/otp/verify/';
export const UPSTREAM_OTP_RESEND_PATH = '/security/otp/resend/';

/** SimpleJWT refresh — the SPK2GO API serves it under `/api/` (same as the back office). */
export const UPSTREAM_TOKEN_REFRESH_PATH = '/api/token/refresh/';
export const CLIENT_TOKEN_REFRESH_PATH = '/api/auth/refresh/';

/** Same-origin browser routes for the OTP step. */
export const CLIENT_OTP_VERIFY_PATH = '/api/auth/otp/verify/';
export const CLIENT_OTP_RESEND_PATH = '/api/auth/otp/resend/';

/** Credentials cookies are unnecessary for JWT bearer login via our proxy. */
export const LOGIN_WITH_CREDENTIALS = false;

/**
 * Browser tablet calls always go here (same origin). The route proxies to
 * upstream `/tablets/` so production domains are not blocked by CORS.
 */
export const CLIENT_TABLETS_PATH = '/api/tablets/';

export const LEADS_ENDPOINT = readEnv(process.env.NEXT_PUBLIC_LEADS_ENDPOINT, '/api/leads/');

export const CONTACT_EMAIL = readEnv(process.env.NEXT_PUBLIC_CONTACT_EMAIL, SITE.email);

export const CONTACT_PHONE = readEnv(process.env.NEXT_PUBLIC_CONTACT_PHONE, '');

export const IS_PRODUCTION = process.env.NODE_ENV === 'production';
